import { DataSource, FinancialPeriodPreparationMethod } from "@inrev/common";
import { DateTime } from "luxon";
import { type FC } from "react";
import { Icon, Text } from "../primitives";
import { ManualPeriodIndicator } from "../ui/Financials/ManualPeriodIndicator";
import { PriorPeriodIndicator } from "../ui/Financials/PriorPeriodIndicator";
import { isAnalysisPeriodSelectorItem, type PeriodSelectorItem } from "./types";
import { isPriorPeriodOnStatement } from "./utils/isPriorPeriodOnStatement";

type FinancialsPeriodSelectorLabelProps = {
	item: PeriodSelectorItem;
	isAdmin: boolean;
};

export const FinancialsPeriodSelectorLabel: FC<FinancialsPeriodSelectorLabelProps> = ({
	item,
	isAdmin,
}) => {
	const isPriorPeriod = isPriorPeriodOnStatement(item.date, item.sourceStatementDate);
	const dateLabel = DateTime.fromISO(item.date).toFormat("MM/dd/yyyy");
	const metadataParts = [
		item.typeLabel,
		FinancialPeriodPreparationMethod.displayName(item.preparationMethod),
	];
	if (item.showRevisionLabel) {
		metadataParts.push(`Uploaded ${DateTime.fromISO(item.createdAt).toFormat("MM/dd/yyyy")}`);
	}

	return (
		<>
			<div className="flex items-center gap-1.5">
				<Text type="body-strong">
					{dateLabel}
					{item.sourceDataSource === DataSource.manual && <ManualPeriodIndicator />}
					{isPriorPeriod && item.sourceStatementDate && (
						<PriorPeriodIndicator sourceStatementDate={item.sourceStatementDate} />
					)}
				</Text>
				{isAdmin && item.showActiveIndicator && (
					<Icon
						data-testid="active-statement-indicator"
						type="check-badge"
						size="mini"
						color="accent"
						tooltip="Active statement"
					/>
				)}
				{isAnalysisPeriodSelectorItem(item) && (
					<span className="flex items-center gap-1">
						{item.hasBalanceSheet && (
							<Text
								type="caption"
								color="secondary"
								className="rounded-sm bg-surface-light px-1"
								data-testid="analysis-badge-bs"
							>
								BS
							</Text>
						)}
						{item.hasIncomeStatement && (
							<Text
								type="caption"
								color="secondary"
								className="rounded-sm bg-surface-light px-1"
								data-testid="analysis-badge-is"
							>
								IS
							</Text>
						)}
					</span>
				)}
			</div>
			<Text type="caption" color="tertiary">
				{metadataParts.join(" · ")}
			</Text>
		</>
	);
};
